import { API_URL, HASH_API_TOKEN } from "config";
import { managedFetch } from "utils/FetchManager";
import { fetchIntermixGeoJSON, withArrowAccept } from "utils/intermixArrow";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useRuns } from "contexts/RunsContext";
import usePage from "hooks/usePage";
import usePrevious from "hooks/usePrevious";
import { useAuth } from "contexts/AuthContext";

// Layer types every deployed api-playground serves, so a 400 on one of them is
// a real error and must be reported. Add "structure_hardening" here once every
// environment serves it, or a genuine 400 on it stays silent.
const LAYERS_SERVED_EVERYWHERE = ["mitigations", "additional_buildings"];

export default function UrbanSimulationDetails() {
  const { runId, isSharePage } = usePage();
  const {
    setIntermixResults,
    intermixRunID,
    setIntermixRunID,
    setAnnotations,
    setIntermixScenarioTree,
    setIntermixScenarioEmbers,
    setIntermixScenarioSequence,
    setIsLoadingIntermixSummary,
    setIsLoadingIntermixExtended,
    selectedIntermixRunIndex,
    setSelectedIntermixRunIndex,
    setIntermixRunMetadata,
    setIntermixRunsList,
    setIntermixMitigations,
    setIntermixStructureHardening,
    setIntermixAdditionalBuildings,
  } = useRuns();

  const previousRunId = usePrevious(runId);
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  // The run the in-flight load belongs to. A load whose run has since changed
  // must not write its answer over the newer one.
  const loadingRunIdRef = useRef(null);
  const BEARER_TOKEN = isSharePage ? HASH_API_TOKEN : currentUser?.accessToken;
  const prefix = isSharePage ? "/hash" : "";
  const filterQuery = isSharePage
    ? `filter_simulation_hash=${runId}`
    : `filter_simulation_id=${runId}`;

  useEffect(() => {
    if (runId === previousRunId) {
      return;
    }
    loadingRunIdRef.current = runId;

    setIsLoadingIntermixSummary(true);
    setIsLoadingIntermixExtended(true);

    // Clear old scenario data and reset run index when switching runs
    setIntermixScenarioTree(null);
    setIntermixScenarioEmbers(null);
    setIntermixScenarioSequence(null);
    setIntermixRunsList(null);
    setSelectedIntermixRunIndex(1);

    if (!runId) {
      setIntermixRunMetadata(null);
      setIsLoadingIntermixSummary(false);
      setIsLoadingIntermixExtended(false);
      return;
    }
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${BEARER_TOKEN}`,
    };
    const fetchSimulationData = async () => {
      try {
        // Composite intermix result is the largest payload; ask for Arrow.
        const resultPromise = fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        const detailsPromise = managedFetch(
          `${API_URL}${prefix}/simulation?${filterQuery}`,
          { headers: authHeaders },
          isSharePage ? null : currentUser
        );
        const runsListPromise = managedFetch(
          `${API_URL}${prefix}/simulation/intermix/${runId}/runs`,
          { headers: authHeaders },
          isSharePage ? null : currentUser
        );
        const annotationPromise = isSharePage
          ? Promise.resolve(null)
          : managedFetch(
              `${API_URL}${prefix}/note/${runId}`,
              { headers: authHeaders },
              currentUser
            );

        const [
          { response: resultResponse, data: resultData },
          detailsResponse,
          runsListResponse,
          annotationResponse,
        ] = await Promise.all([
          resultPromise,
          detailsPromise,
          runsListPromise,
          annotationPromise,
        ]);

        if (loadingRunIdRef.current !== runId) {
          return;
        }

        if (
          !resultResponse?.ok ||
          !detailsResponse.ok ||
          (!isSharePage && !annotationResponse?.ok)
        ) {
          return;
        }

        const [detailsData, runsListData, annotationData] = await Promise.all([
          detailsResponse.json(),
          runsListResponse?.ok
            ? runsListResponse.json()
            : Promise.resolve(null),
          annotationResponse
            ? annotationResponse.json()
            : Promise.resolve(null),
        ]);

        if (resultData?.created_at) {
          resultData.created_at = new Date(resultData.created_at);
        }
        const sim = detailsData?.simulations?.[0];
        if (sim) {
          sim.created_at = new Date(sim.created_at);
        }
        if (annotationData) {
          annotationData.created_at = new Date(annotationData.created_at);
        }

        if (resultData && resultData.type === "FeatureCollection") {
          setIntermixResults(resultData);
        }

        setIntermixRunsList(runsListData?.runs ?? []);
        setIntermixRunID(runId);

        if (annotationData) {
          setAnnotations(annotationData);
        }

        if (sim) {
          setIntermixRunMetadata({
            durationMinutes: sim.duration ?? 240,
            iterations: sim.iterations ?? 1,
          });
        } else {
          setIntermixRunMetadata(null);
        }
        setIsLoadingIntermixSummary(false);
      } catch (error) {
        console.error("Error fetching simulation data:", error);
        setIsLoadingIntermixSummary(false);
        setIsLoadingIntermixExtended(false);
      } finally {
        setIsLoadingIntermixExtended(false);
      }
    };

    fetchSimulationData();
  }, [
    previousRunId,
    runId,
    setIntermixResults,
    setIntermixRunID,
    setAnnotations,
    setIntermixScenarioTree,
    setIntermixScenarioEmbers,
    setIntermixScenarioSequence,
    setIsLoadingIntermixSummary,
    setIsLoadingIntermixExtended,
    setSelectedIntermixRunIndex,
    setIntermixRunMetadata,
    setIntermixRunsList,
    BEARER_TOKEN,
    prefix,
    filterQuery,
    currentUser,
    isSharePage,
  ]);

  // Keep input loading independent so leaving the view invalidates only this
  // load, without changing the existing result loader or clearing cached data.
  useEffect(() => {
    let cancelled = false;
    setIntermixMitigations(null);
    setIntermixStructureHardening(null);
    setIntermixAdditionalBuildings(null);
    if (!runId) return;

    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${BEARER_TOKEN}`,
    };
    // Input geometries attached to the request. Most runs have none, so a
    // 404 is the normal answer and must not fail the load. Any other
    // failure is reported rather than rendered as an absent layer.
    const failedLayers = [];
    const fetchInputLayer = async (layerType) => {
      try {
        const response = await managedFetch(
          `${API_URL}${prefix}/simulation/intermix/${runId}/${layerType}`,
          { headers: authHeaders },
          isSharePage ? null : currentUser
        );
        // managedFetch resolves to undefined on an aborted request, and a
        // 404 is a run without this layer.
        if (!response || response.status === 404) {
          return null;
        }
        // A 400 on a layer type not yet served everywhere means an
        // api-playground that does not know it. The one other 400 this
        // endpoint ever raised needed two intermix_runs rows for one
        // simulation, which a unique constraint forbids.
        if (
          response.status === 400 &&
          !LAYERS_SERVED_EVERYWHERE.includes(layerType)
        ) {
          return null;
        }
        if (!response.ok) {
          failedLayers.push(layerType);
          console.error(
            `Error fetching ${layerType} layer: ${response.status}`
          );
          return null;
        }
        return await response.json();
      } catch (error) {
        failedLayers.push(layerType);
        console.error(`Error fetching ${layerType} layer:`, error);
        return null;
      }
    };

    // Input layers load independently of model results.
    Promise.all([
      fetchInputLayer("mitigations"),
      fetchInputLayer("structure_hardening"),
      fetchInputLayer("additional_buildings"),
    ])
      .then(
        ([
          mitigationsData,
          structureHardeningData,
          additionalBuildingsData,
        ]) => {
          if (cancelled) {
            return;
          }

          setIntermixMitigations(mitigationsData);
          setIntermixStructureHardening(structureHardeningData);
          setIntermixAdditionalBuildings(additionalBuildingsData);

          if (failedLayers.length) {
            enqueueSnackbar(
              `Could not load ${failedLayers.join(", ")} for this run`,
              { variant: "warning" }
            );
          }
        }
      )
      // Handle errors from applying the layer data separately from fetching it.
      .catch((error) => {
        console.error("Error handling input layers:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [
    runId,
    BEARER_TOKEN,
    prefix,
    currentUser,
    isSharePage,
    enqueueSnackbar,
    setIntermixMitigations,
    setIntermixStructureHardening,
    setIntermixAdditionalBuildings,
  ]);

  // Fetch tree and nodes only for the selected run index (aggregate already loaded)
  useEffect(() => {
    if (!runId || !selectedIntermixRunIndex || intermixRunID !== runId) {
      return;
    }

    let cancelled = false;
    setIsLoadingIntermixExtended(true);
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${BEARER_TOKEN}`,
    };

    const fetchTreeAndNodes = async () => {
      try {
        const { response, data } = await fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}/${selectedIntermixRunIndex}/tree`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        if (cancelled) return;
        setIntermixScenarioTree(
          response?.ok && data?.type === "FeatureCollection" ? data : null
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching run tree:", error);
          setIntermixScenarioTree(null);
        }
      }

      if (cancelled) return;

      try {
        const { response, data } = await fetchIntermixGeoJSON(
          managedFetch(
            `${API_URL}${prefix}/simulation/intermix/${runId}/${selectedIntermixRunIndex}/nodes`,
            withArrowAccept({ headers: authHeaders }),
            isSharePage ? null : currentUser
          )
        );
        if (cancelled) return;
        setIntermixScenarioSequence(
          response?.ok && data?.type === "FeatureCollection" ? data : null
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching run nodes:", error);
          setIntermixScenarioSequence(null);
        }
      }

      if (!cancelled) {
        setIsLoadingIntermixExtended(false);
      }
    };

    fetchTreeAndNodes();
    return () => {
      cancelled = true;
    };
  }, [
    runId,
    selectedIntermixRunIndex,
    intermixRunID,
    setIntermixScenarioTree,
    setIntermixScenarioSequence,
    setIsLoadingIntermixExtended,
    BEARER_TOKEN,
    prefix,
    currentUser,
    isSharePage,
  ]);
  return null;
}
